//@flow
import React, {useState} from 'react';
import {
  TableRow,
  TableCell,
  makeStyles,
  Chip,
  IconButton,
} from '@material-ui/core';

import type {ContactSummaryItem} from '../../../../entities/crm/contact';

import ConversationLabel from '../../chat/components/MultiChat/ConversationLabel/ConversationLabel';
import {getStringDate} from '../../../../utils/date';
import {colors} from '../../../../assets/styles/colors';
import {Edit} from '@material-ui/icons';
import {useToggle} from '../../../../utils/react';
import {getMultichatServiceIcon} from '../../utils/service-icon';
import CustomChip from '../../../../elements/CustomChip';
import {formatPhoneNumber} from '../../../../utils/phone';
import EditContactModal from './EditContactModal';
import AddConversationLabelModal from '../../chat/components/Modals/AddConversationLabelModal';
import {useSetTagToConversation} from '../../chat/utils/conversation';

type Props = {
  contact: ContactSummaryItem,
};

const useStyles = makeStyles(theme => ({
  channelIcon: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    minHeight: '24px',
    borderRadius: '50%',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  },
  tableFilters: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '.5rem',
    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  filters: {
    display: 'flex',
    gap: '.5rem',
  },
  filterButton: {
    padding: '.5rem .75rem',
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '2rem',
    color: colors.grey700,
    border: `1px solid ${colors.grey300}`,
  },
  conversationTags: {
    display: 'flex',
    gap: '.5rem',
    maxWidth: '200px',
    overflow: 'auto',
  },
  rowItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  rowItemTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    flexWrap: 'wrap',
  },
}));

const formatServiceIdentifier = (deviceType: string, identifier: string) => {
  if (deviceType === 'WHATSAPP_WEB' || deviceType === 'WHATSAPP_API') {
    return formatPhoneNumber(`+${identifier}`);
  }

  if (deviceType === 'FB_MESSENGER' || deviceType === 'INSTAGRAM') {
    return identifier;
  }

  return 'Desconocido';
};

export default function ContactTableRow(props: Props) {
  const {contact} = props;
  const classes = useStyles();

  const {setTagToConversation} = useSetTagToConversation(
    contact.crmConversationId
  );

  const [labelModalOpen, toggleLabelModalOpen] = useToggle(false);
  const [contactName, setContactName] = useState(contact.name);
  const [editContactModalOpen, setEditContactModalOpen] = useState(false);

  const [contactTags, setContactTags] = useState(contact.tags);

  const handleRemoveTag = (tag: string) => {
    const newTags = contactTags.filter(t => t.id !== tag);
    const newTagsIds = newTags.map(t => t.id);
    setTagToConversation(newTagsIds).then(() => {
      setContactTags(newTags);
    });
  };
  const serviceName = contact.ravenServiceId;
  const icon = getMultichatServiceIcon(serviceName);
  return (
    <>
      <TableRow>
        <TableCell component="th" scope="row" align="center">
          {getStringDate(contact.createdAt)}
        </TableCell>
        <TableCell align="center">
          <div className={classes.rowItem}>
            <span>{contactName || 'Sin nombre'}</span>
            <IconButton
              size="small"
              onClick={() => setEditContactModalOpen(true)}
            >
              <Edit size="small" />
            </IconButton>
          </div>
        </TableCell>
        <TableCell align="center">{contact.numberOfOrders}</TableCell>
        <TableCell align="center">
          <div className={classes.rowItem}>
            <picture className={classes.channelIcon}>
              <img src={icon} alt="" />
            </picture>

            {formatServiceIdentifier(serviceName, contact.identifier)}
          </div>
        </TableCell>
        <TableCell align="center">
          <CustomChip
            color={contact.read ? 'primary' : 'secondary'}
            label={contact.read ? 'No' : 'Si'}
          />
        </TableCell>
        <TableCell align="left">
          <div className={classes.rowItemTag}>
            {contact.tags.map(tag => (
              <ConversationLabel
                key={tag.id}
                label={tag.tag}
                onDelete={() => {
                  handleRemoveTag(tag.id);
                }}
              />
            ))}
            <Chip
              className={classes.chip}
              label="Agregar +"
              variant="outlined"
              clickable
              onClick={toggleLabelModalOpen}
            />
          </div>
        </TableCell>
      </TableRow>

      <EditContactModal
        open={editContactModalOpen}
        onClose={() => setEditContactModalOpen(false)}
        onSave={name => {
          setContactName(name);
        }}
        name={contactName ?? contact.name}
        identifier={contact.identifier}
      />

      {labelModalOpen && (
        <AddConversationLabelModal
          open={labelModalOpen}
          //$FlowFixMe
          tags={contactTags}
          onSave={params => {
            setTagToConversation(params.map(tag => tag.id));
            //$FlowFixMe
            setContactTags(params);
          }}
          handleClose={toggleLabelModalOpen}
        />
      )}
    </>
  );
}
